import React, { useEffect, useState } from 'react';
import { isComingSoon, getItemStatus, toJsDate, formatEpisodeLabel, isEpisodeConfirmed, isEpisodeMissing } from '../utils/dateUtils';
import { fetchAllMedia } from '../utils/mediaUtils';
import MediaCard from '../components/MediaCard';
import LoadingSpinner from '../components/LoadingSpinner';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';

function getComingSoonSortDate(item) {
  if (item.type === 'movie') {
    const streaming = toJsDate(item.streamingReleaseDate);
    const dvd = toJsDate(item.dvdReleaseDate);
    if (streaming && dvd) return streaming < dvd ? streaming : dvd;
    if (streaming) return streaming;
    if (dvd) return dvd;
    return null;
  }
  return toJsDate(item.releaseDate);
}

function ComingSoon({ onItemClick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const all = await fetchAllMedia();

        const expanded = all.flatMap(item => {
          const status = getItemStatus(item);
          if (status === 'missing') return [];

          if (item.type !== 'tv') {
            if (status === 'coming_soon') return [item];
            if (status === 'in_theatres') return [{ ...item, inTheatres: true }];
            return [];
          }

          const now = new Date();
          const episodesWithDates = (item.episodes || []).filter(ep => ep.airDate);
          const pendingEpisodeSource = episodesWithDates
            .filter(ep => !isEpisodeConfirmed(ep) && !isEpisodeMissing(ep));

          const episodesBySeason = episodesWithDates.reduce((acc, ep) => {
            const seasonNumber = Number(ep.seasonNumber);
            if (!acc[seasonNumber]) acc[seasonNumber] = [];
            acc[seasonNumber].push(ep);
            return acc;
          }, {});

          const pendingBySeason = pendingEpisodeSource.reduce((acc, ep) => {
            const seasonNumber = Number(ep.seasonNumber);
            if (!acc[seasonNumber]) acc[seasonNumber] = [];
            acc[seasonNumber].push(ep);
            return acc;
          }, {});

          const completeSeasonNumbers = Object.keys(episodesBySeason)
            .map(Number)
            .filter(seasonNumber => {
              const seasonEpisodes = episodesBySeason[seasonNumber] || [];
              const pendingSeasonEpisodes = pendingBySeason[seasonNumber] || [];
              return seasonEpisodes.length > 0 && pendingSeasonEpisodes.length === seasonEpisodes.length;
            });

          const allSeasonsComingSoon = completeSeasonNumbers.length > 0
            && completeSeasonNumbers.length === Object.keys(episodesBySeason).length;

          if (allSeasonsComingSoon) {
            const earliestEpisode = pendingEpisodeSource
              .map(ep => toJsDate(ep.airDate))
              .filter(Boolean)
              .sort((a, b) => a - b)[0];
            const isOnlySeasonOne = completeSeasonNumbers.length === 1 && completeSeasonNumbers[0] === 1;

            return [{
              ...item,
              id: `${item.id}-all-seasons`,
              releaseDate: earliestEpisode || item.releaseDate,
              episodeLabel: isOnlySeasonOne ? 'Season 1' : 'All Seasons',
              showItem: item,
            }];
          }

          const completeSeasonSet = new Set(completeSeasonNumbers);
          const seasonCards = completeSeasonNumbers.map(seasonNumber => {
            const seasonEpisodes = pendingBySeason[seasonNumber] || [];
            const earliestEpisode = seasonEpisodes
              .map(ep => toJsDate(ep.airDate))
              .filter(Boolean)
              .sort((a, b) => a - b)[0];

            return {
              ...item,
              id: `${item.id}-season-${seasonNumber}`,
              releaseDate: earliestEpisode || item.releaseDate,
              episodeLabel: `Season ${seasonNumber}`,
              isEpisodeEntry: true,
              isAiredEpisode: earliestEpisode !== undefined && earliestEpisode !== null && earliestEpisode <= now,
              showItem: item,
            };
          });

          const episodeCards = pendingEpisodeSource
            .filter(ep => !completeSeasonSet.has(Number(ep.seasonNumber)))
            .map(ep => {
              const airDate = toJsDate(ep.airDate);
              return {
                ...item,
                id: `${item.id}-${ep.seasonNumber}-${ep.episodeNumber}`,
                releaseDate: ep.airDate,
                episodeLabel: formatEpisodeLabel(ep),
                isEpisodeEntry: true,
                isAiredEpisode: airDate !== null && airDate <= now,
                showItem: item,
              };
            });

          const pendingEpisodes = [...seasonCards, ...episodeCards];

          if (pendingEpisodes.length > 0) return pendingEpisodes;
          return isComingSoon(item) ? [item] : [];
        });

        expanded.sort((a, b) => {
          const dateA = getComingSoonSortDate(a);
          const dateB = getComingSoonSortDate(b);
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA - dateB;
        });
        setItems(expanded);
      } catch (err) {
        setError('Failed to load coming soon items.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, []);

  if (loading) return <LoadingSpinner message="Loading coming soon…" />;

  return (
    <Container maxWidth={false} disableGutters sx={{ py: 4 }}>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {items.length === 0 && !error ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">Nothing coming soon yet.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5 }}>
          {items.map(item => (
            <Box key={item.id}>
              <MediaCard
                item={item}
                subtitle={item.type === 'tv' ? (item.episodeLabel || '') : undefined}
                onClick={() => onItemClick && onItemClick(item.showItem || item)}
              />
              {item.inTheatres ? (
                <Typography
                  variant="caption"
                  color="success.main"
                  fontWeight={700}
                  sx={{ display: 'block', mt: 0.75, textAlign: 'center' }}
                >
                  🎬 In Theatres
                </Typography>
              ) : item.isAiredEpisode ? (
                <Typography
                  variant="caption"
                  color="warning.main"
                  fontWeight={700}
                  sx={{ display: 'block', mt: 0.75, textAlign: 'center' }}
                >
                  📺 Aired
                </Typography>
              ) : toJsDate(item.releaseDate) && (
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{ display: 'block', mt: 0.75, textAlign: 'center' }}
                >
                  {toJsDate(item.releaseDate).toLocaleDateString('en-AU', {
                    day: 'numeric', month: 'short', year: 'numeric',
                  })}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Container>
  );
}

export default ComingSoon;
